import { NetService } from 'services/net-service';
import { autoinject, observable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController, ValidationRules, validateTrigger } from 'aurelia-validation';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { ToastService } from 'services/toast-service';
import { Router } from 'aurelia-router';
import { StandardValidationBuilder } from 'lib/validation';

interface NetIdentifierItem {
  Id: number;
  Value: string;
}

@autoinject
export class NetCopy {
  private net!: Models.Net;

  isCopying = false;

  @observable numberOfCopies = 1;

  copydata!: { ExistingNetId: number; Prefix?: string };

  identifierItems: NetIdentifierItem[] = [];

  constructor(
    private netService: NetService,
    private errorService: ErrorService,
    private i18n: I18N,
    private toastService: ToastService,
    protected validationController: ValidationController,
    private router: Router,
    private ruleBuilder: StandardValidationBuilder
  ) {}

  async activate(params: { Id: string | number }) {
    this.validationController.validateTrigger = validateTrigger.changeOrBlur;
    this.applyValidationRules(this);
    await this.getExistingNets(params.Id);
  }

  protected applyValidationRules(item: NetCopy) {
    ValidationRules
      .ensure('numberOfCopies')
      .required().withMessage(this.i18n.tr('general.requiredField'))
      .min(1).withMessage(this.i18n.tr('validation.mustBeGreaterOrEqualToValue', { value: 1 }))
      .max(30).withMessage(this.i18n.tr('validation.mustBeLessOrEqualToValue', { value: 30 }))
      .on(item);
  }

  newNetIdentifierAt(index: number) {
    const value = this.identifierItems[index]?.Value ?? '';
    return `${this.copydata?.Prefix?.toUpperCase() ?? ''} - ${value}`;
  }

  numberOfCopiesChanged(newValue: number | string) {
    const parsed = Math.floor(Number(newValue));
    this.applyValidationRules(this);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 30) return;
    if (!this.copydata) return;
    this.syncIdentifierItems(parsed);
  }

  private syncIdentifierItems(count: number) {
    if (this.identifierItems.length === count) return;

    if (this.identifierItems.length < count) {
      const next = [...this.identifierItems];
      for (let i = this.identifierItems.length; i < count; i++) {
        // Start Id at 1 — `validation-controller-errors` treats a falsy `itemId`
        // (like the number 0) as "no id" and then shows all errors for the
        // property name, which would make the first row display every error.
        const item: NetIdentifierItem = { Id: i + 1, Value: '' };
        next.push(item);
        this.addIdentifierValidationRules(item);
      }
      this.identifierItems = next;
    } else {
      this.identifierItems = this.identifierItems.slice(0, count);
    }
  }

  private addIdentifierValidationRules(item: NetIdentifierItem) {
    this.ruleBuilder
      .with(item, item.Id.toString())
      .required('Value')
      .done();
  }

  async getExistingNets(netId: number | string) {
    try {
      const net = await this.netService.get(netId);
      this.net = net;
      this.copydata = {
        ExistingNetId: this.net.Id,
        Prefix: undefined,
      };

      this.ruleBuilder.with(this.copydata).required('Prefix').done();

      const seperatorIndex = this.net.NetIdentifier.indexOf('-');
      const spaceIndex = this.net.NetIdentifier.indexOf(' ');
      if (seperatorIndex > 0 || spaceIndex > 0) {
        const prefix = this.net.NetIdentifier.substring(0, seperatorIndex > 0 ? seperatorIndex : spaceIndex);
        this.copydata.Prefix = prefix;
      } else {
        this.copydata.Prefix = this.net.NetIdentifier;
      }

      this.identifierItems = [];
      this.syncIdentifierItems(this.numberOfCopies);
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  async copyNet() {
    const { valid } = await this.validationController.validate();
    if (!valid) return;

    const trimmedValues = this.identifierItems.map((i) => i.Value.trim().toLowerCase());
    if (new Set(trimmedValues).size !== trimmedValues.length) {
      this.toastService.showError('net.copyNetOneOrMoreAlreadyExistingIdentifiers');
      return;
    }

    this.isCopying = true;

    const prefix = (this.copydata.Prefix ?? '').trim();
    const fullIdentifiers = this.identifierItems.map((i) => `${prefix} - ${i.Value.trim()}`);

    try {
      const duplicates = await this.netService.checkNetIdentifiers(fullIdentifiers);

      if (!!duplicates && duplicates.length !== 0) {
        this.toastService.showError('net.copyNetOneOrMoreAlreadyExistingIdentifiers');
        this.isCopying = false;
        return;
      }

      const result = (await this.netService.copyNet({
        ExistingNetId: this.copydata.ExistingNetId,
        NewNetIdentifiers: fullIdentifiers,
      })) as unknown as { Ids: number[] };

      this.toastService.show('info', 'net.netcopied');

      const firstId = result?.Ids?.[0];
      if (firstId) {
        this.router.navigateToRoute('net-detail', { Id: firstId });
      }
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isCopying = false;
    }
  }
}
